import React, { FC } from 'react';
import ReactDOM from 'react-dom';
import styled, { css } from 'styled-components';

import { NgSideNavIcon } from './NgSideNavIcon';

export interface ResolvedFlatFlyout {
  kind: 'flat';
  title: string;
  titleSlug?: string;
  items: any[];
}

export interface ResolvedSectionedFlyout {
  kind: 'sectioned';
  title: string;
  sections: { header: string; items: any[] }[];
}

export interface ResolvedTooltip {
  kind: 'tooltip';
  label: string;
  onClickNavigate?: () => void;
}

export type ResolvedFlyout = ResolvedFlatFlyout | ResolvedSectionedFlyout | ResolvedTooltip;

export interface ActiveFlyout {
  key: string;
  itemTop: number;
  itemCenterY: number;
  resolved: ResolvedFlyout;
}

const leftArrow = css`
  &::before {
    content: '';
    position: absolute;
    left: -6px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-right: 6px solid ${({ theme }) => theme?.palette?.overlayBackground};
  }
`;

const TooltipBubble = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  transform: translateY(-50%);
  z-index: 10;
  pointer-events: auto;
  white-space: nowrap;
  color: ${({ theme }) => theme.palette.white};
  font-size: ${({ theme }) => theme?.fontSize?.default};
  font-family: ${({ theme }) => theme?.fontFamily?.main};
  padding: ${({ theme }) => `${theme.spacing.default} ${theme.spacing.default}`};
  border-radius: ${({ theme }) => theme?.radius?.xSmall};
  background: ${({ theme }) => theme?.palette?.overlayBackground};
  cursor: pointer;
  ${leftArrow}
`;

const FlyoutContainer = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  z-index: 20;
  pointer-events: auto;
  background: ${({ theme }) => theme?.palette?.overlayBackground};
  border: 1px solid ${({ theme }) => theme?.palette?.overlayBorder};
  border-radius: ${({ theme }) => theme?.radius?.small};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  padding: ${({ theme }) => theme?.spacing?.default};
  min-width: 200px;
  font-family: ${({ theme }) => theme?.fontFamily?.main};

  &::before {
    content: '';
    position: absolute;
    left: -8px;
    top: 20px;
    width: 0;
    height: 0;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-right: 8px solid ${({ theme }) => theme?.palette?.overlayBackground};
  }
`;

const FlyoutTitle = styled.div<{ $clickable: boolean }>`
  font-weight: 600;
  font-size: ${({ theme }) => theme?.fontSize?.default};
  color: ${({ theme }) => theme.palette.white};
  margin-bottom: 12px;
  padding: 4px 0 12px;
  border-bottom: 1px solid ${({ theme }) => theme?.palette?.overlayBorder};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  ${({ $clickable, theme }) =>
    $clickable &&
    css`
      &:hover {
        color: ${theme?.palette?.overlayAccent};
      }
    `}
`;

const FlyoutSectionHeader = styled.div<{ $first: boolean }>`
  padding: 12px 12px 4px 12px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.palette.white};
  user-select: none;

  ${({ $first }) =>
    !$first &&
    css`
      border-top: 1px solid ${({ theme }) => theme?.palette?.overlayBorder};
      margin-top: 4px;
      padding-top: 12px;
    `}
`;

const FlyoutItemRow = styled.div<{ $active?: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;

  border-radius: ${({ theme }) => theme?.radius?.xSmall};
  color: ${({ $active, theme }) => ($active ? theme.palette.white : theme?.palette?.overlayText)};
  font-size: ${({ theme }) => theme?.fontSize?.default};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  background: ${({ $active, theme }) => ($active ? theme?.palette?.overlayItemActive : 'transparent')};
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${({ theme }) => theme?.palette?.overlayItemHover};
    color: ${({ theme }) => theme.palette.white};
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: ${({ theme }) => theme?.palette?.overlayText};
    stroke: currentColor;
    fill: none;
  }
`;

interface NavFlyoutProps {
  activeFlyout: ActiveFlyout;
  flyoutLeft: number;
  currentPath: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onItemClick: (item: any) => void;
  onTitleClick: (slug: string) => void;
}

const normalizeSlug = (slug: string) => (slug.startsWith('/') ? slug : `/${slug}`);

export const NavFlyout: FC<NavFlyoutProps> = ({
  activeFlyout,
  flyoutLeft,
  currentPath,
  onMouseEnter,
  onMouseLeave,
  onItemClick,
  onTitleClick,
}) => {
  const { resolved, itemTop, itemCenterY } = activeFlyout;

  if (resolved.kind === 'tooltip') {
    return ReactDOM.createPortal(
      <TooltipBubble
        $top={itemCenterY}
        $left={flyoutLeft}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={resolved.onClickNavigate}
      >
        {resolved.label}
      </TooltipBubble>,
      document.body,
    );
  }

  return ReactDOM.createPortal(
    <FlyoutContainer $top={itemTop} $left={flyoutLeft} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <FlyoutTitle
        $clickable={resolved.kind === 'flat' && !!resolved.titleSlug}
        onClick={() => resolved.kind === 'flat' && resolved.titleSlug && onTitleClick(resolved.titleSlug)}
      >
        {resolved.title}
      </FlyoutTitle>

      {resolved.kind === 'flat' &&
        resolved.items.map((item: any) => (
          <FlyoutItemRow
            key={item.slug}
            $active={currentPath.startsWith(normalizeSlug(item.slug))}
            onClick={() => onItemClick(item)}
          >
            <NgSideNavIcon icon={item.icon} />
            {item.displayName}
          </FlyoutItemRow>
        ))}

      {resolved.kind === 'sectioned' &&
        resolved.sections.map((section, sectionIndex) => (
          <React.Fragment key={section.header}>
            <FlyoutSectionHeader $first={sectionIndex === 0}>{section.header}</FlyoutSectionHeader>
            {section.items.map((item: any) => (
              <FlyoutItemRow
                key={item.slug}
                $active={currentPath.startsWith(normalizeSlug(item.slug))}
                onClick={() => onItemClick(item)}
              >
                <NgSideNavIcon icon={item.icon} />
                {item.displayName}
              </FlyoutItemRow>
            ))}
          </React.Fragment>
        ))}
    </FlyoutContainer>,
    document.body,
  );
};
